import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Book,
  Search,
  BookOpen,
  ChevronRight,
  ChevronLeft,
  Loader2,
  FileText,
  Plus,
  Calendar,
  Save,
  Trash2,
  Edit,
  X
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { sermonNotesAPI, bibleAPI } from '../services/api';

// Bible books data
const BIBLE_BOOKS = {
  oldTestament: [
    { name: 'Genesis', chapters: 50, abbrev: 'gen' },
    { name: 'Exodus', chapters: 40, abbrev: 'exod' },
    { name: 'Leviticus', chapters: 27, abbrev: 'lev' },
    { name: 'Numbers', chapters: 36, abbrev: 'num' },
    { name: 'Deuteronomy', chapters: 34, abbrev: 'deut' },
    { name: 'Joshua', chapters: 24, abbrev: 'josh' },
    { name: 'Judges', chapters: 21, abbrev: 'judg' },
    { name: 'Ruth', chapters: 4, abbrev: 'ruth' },
    { name: '1 Samuel', chapters: 31, abbrev: '1sam' },
    { name: '2 Samuel', chapters: 24, abbrev: '2sam' },
    { name: '1 Kings', chapters: 22, abbrev: '1kgs' },
    { name: '2 Kings', chapters: 25, abbrev: '2kgs' },
    { name: '1 Chronicles', chapters: 29, abbrev: '1chr' },
    { name: '2 Chronicles', chapters: 36, abbrev: '2chr' },
    { name: 'Ezra', chapters: 10, abbrev: 'ezra' },
    { name: 'Nehemiah', chapters: 13, abbrev: 'neh' },
    { name: 'Esther', chapters: 10, abbrev: 'esth' },
    { name: 'Job', chapters: 42, abbrev: 'job' },
    { name: 'Psalms', chapters: 150, abbrev: 'ps' },
    { name: 'Proverbs', chapters: 31, abbrev: 'prov' },
    { name: 'Ecclesiastes', chapters: 12, abbrev: 'eccl' },
    { name: 'Song of Solomon', chapters: 8, abbrev: 'song' },
    { name: 'Isaiah', chapters: 66, abbrev: 'isa' },
    { name: 'Jeremiah', chapters: 52, abbrev: 'jer' },
    { name: 'Lamentations', chapters: 5, abbrev: 'lam' },
    { name: 'Ezekiel', chapters: 48, abbrev: 'ezek' },
    { name: 'Daniel', chapters: 12, abbrev: 'dan' },
    { name: 'Hosea', chapters: 14, abbrev: 'hos' },
    { name: 'Joel', chapters: 3, abbrev: 'joel' },
    { name: 'Amos', chapters: 9, abbrev: 'amos' },
    { name: 'Obadiah', chapters: 1, abbrev: 'obad' },
    { name: 'Jonah', chapters: 4, abbrev: 'jonah' },
    { name: 'Micah', chapters: 7, abbrev: 'mic' },
    { name: 'Nahum', chapters: 3, abbrev: 'nah' },
    { name: 'Habakkuk', chapters: 3, abbrev: 'hab' },
    { name: 'Zephaniah', chapters: 3, abbrev: 'zeph' },
    { name: 'Haggai', chapters: 2, abbrev: 'hag' },
    { name: 'Zechariah', chapters: 14, abbrev: 'zech' },
    { name: 'Malachi', chapters: 4, abbrev: 'mal' },
  ],
  newTestament: [
    { name: 'Matthew', chapters: 28, abbrev: 'matt' },
    { name: 'Mark', chapters: 16, abbrev: 'mark' },
    { name: 'Luke', chapters: 24, abbrev: 'luke' },
    { name: 'John', chapters: 21, abbrev: 'john' },
    { name: 'Acts', chapters: 28, abbrev: 'acts' },
    { name: 'Romans', chapters: 16, abbrev: 'rom' },
    { name: '1 Corinthians', chapters: 16, abbrev: '1cor' },
    { name: '2 Corinthians', chapters: 13, abbrev: '2cor' },
    { name: 'Galatians', chapters: 6, abbrev: 'gal' },
    { name: 'Ephesians', chapters: 6, abbrev: 'eph' },
    { name: 'Philippians', chapters: 4, abbrev: 'phil' },
    { name: 'Colossians', chapters: 4, abbrev: 'col' },
    { name: '1 Thessalonians', chapters: 5, abbrev: '1thess' },
    { name: '2 Thessalonians', chapters: 3, abbrev: '2thess' },
    { name: '1 Timothy', chapters: 6, abbrev: '1tim' },
    { name: '2 Timothy', chapters: 4, abbrev: '2tim' },
    { name: 'Titus', chapters: 3, abbrev: 'titus' },
    { name: 'Philemon', chapters: 1, abbrev: 'phlm' },
    { name: 'Hebrews', chapters: 13, abbrev: 'heb' },
    { name: 'James', chapters: 5, abbrev: 'jas' },
    { name: '1 Peter', chapters: 5, abbrev: '1pet' },
    { name: '2 Peter', chapters: 3, abbrev: '2pet' },
    { name: '1 John', chapters: 5, abbrev: '1john' },
    { name: '2 John', chapters: 1, abbrev: '2john' },
    { name: '3 John', chapters: 1, abbrev: '3john' },
    { name: 'Jude', chapters: 1, abbrev: 'jude' },
    { name: 'Revelation', chapters: 22, abbrev: 'rev' },
  ]
};

const ALL_BOOKS = [...BIBLE_BOOKS.oldTestament, ...BIBLE_BOOKS.newTestament];

const BiblePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('bible'); // 'bible' or 'notes'
  const [translation, setTranslation] = useState('kjv');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Bible reading state
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapterContent, setChapterContent] = useState(null);
  const [loadingChapter, setLoadingChapter] = useState(false);
  const [showChapterDialog, setShowChapterDialog] = useState(false);
  const [showReadingView, setShowReadingView] = useState(false);
  
  // Sermon notes state
  const [sermonNotes, setSermonNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [savingNote, setSavingNote] = useState(false);
  const [noteForm, setNoteForm] = useState({
    date: new Date().toISOString().split('T')[0],
    topic: '',
    scriptureReference: '',
    notes: ''
  });

  // Fetch sermon notes on mount
  useEffect(() => {
    if (activeTab === 'notes') {
      fetchSermonNotes();
    }
  }, [activeTab]);

  useEffect(() => {
    bibleAPI.markVisited().catch(() => {});
  }, []);

  const fetchSermonNotes = async () => {
    setLoadingNotes(true);
    try {
      const notes = await sermonNotesAPI.getAll();
      setSermonNotes(notes);
    } catch (error) {
      console.error('Error fetching sermon notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  };

  // Fetch chapter content
  const fetchChapter = useCallback(async (book, chapter) => {
    setLoadingChapter(true);
    setShowChapterDialog(false);
    setShowReadingView(true);
    try {
      const reference = `${book.name}+${chapter}`;
      const response = await fetch(
        `https://bible-api.com/${encodeURIComponent(reference)}?translation=${translation}`
      );
      const data = await response.json();
      setChapterContent(data);
    } catch (error) {
      console.error('Error fetching chapter:', error);
      setChapterContent({ error: 'Failed to load chapter. Please try again.' });
    } finally {
      setLoadingChapter(false);
    }
  }, [translation]);

  // Handle book click - open chapter selection dialog
  const handleBookClick = (book) => {
    setSelectedBook(book);
    setShowChapterDialog(true);
  };

  // Handle chapter click - fetch and display chapter
  const handleChapterClick = (chapter) => {
    setSelectedChapter(chapter);
    fetchChapter(selectedBook, chapter);
  };

  // Back to book list
  const backToBooks = () => {
    setShowReadingView(false);
    setChapterContent(null);
    setSelectedBook(null);
    setSelectedChapter(null);
  };

  // Search scriptures
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setSearchResults(null);
    try {
      const response = await fetch(
        `https://bible-api.com/${encodeURIComponent(searchQuery)}?translation=${translation}`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching:', error);
      setSearchResults({ error: 'Search failed. Try a reference like "John 3:16" or "Psalm 23"' });
    } finally {
      setSearching(false);
    }
  };

  // Navigate chapters
  const goToNextChapter = () => {
    if (!selectedBook || !selectedChapter) return;
    if (selectedChapter < selectedBook.chapters) {
      const newChapter = selectedChapter + 1;
      setSelectedChapter(newChapter);
      fetchChapter(selectedBook, newChapter);
    } else {
      // Go to next book
      const currentIndex = ALL_BOOKS.findIndex(b => b.name === selectedBook.name);
      if (currentIndex < ALL_BOOKS.length - 1) {
        const nextBook = ALL_BOOKS[currentIndex + 1];
        setSelectedBook(nextBook);
        setSelectedChapter(1);
        fetchChapter(nextBook, 1);
      }
    }
  };

  const goToPrevChapter = () => {
    if (!selectedBook || !selectedChapter) return;
    if (selectedChapter > 1) {
      const newChapter = selectedChapter - 1;
      setSelectedChapter(newChapter);
      fetchChapter(selectedBook, newChapter);
    } else {
      // Go to previous book's last chapter
      const currentIndex = ALL_BOOKS.findIndex(b => b.name === selectedBook.name);
      if (currentIndex > 0) {
        const prevBook = ALL_BOOKS[currentIndex - 1];
        setSelectedBook(prevBook);
        setSelectedChapter(prevBook.chapters);
        fetchChapter(prevBook, prevBook.chapters);
      }
    }
  };

  // Sermon note handlers
  const handleSaveNote = async (e) => {
    e.preventDefault();
    setSavingNote(true);
    try {
      if (editingNote) {
        const updated = await sermonNotesAPI.update(editingNote.id, noteForm);
        setSermonNotes(sermonNotes.map(n => n.id === editingNote.id ? updated : n));
      } else {
        const newNote = await sermonNotesAPI.create(noteForm);
        setSermonNotes([newNote, ...sermonNotes]);
      }
      setShowNoteDialog(false);
      resetNoteForm();
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this sermon note?')) return;
    try {
      await sermonNotesAPI.delete(noteId);
      setSermonNotes(sermonNotes.filter(n => n.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  const resetNoteForm = () => {
    setNoteForm({
      date: new Date().toISOString().split('T')[0],
      topic: '',
      scriptureReference: '',
      notes: ''
    });
    setEditingNote(null);
  };

  const openEditNote = (note) => {
    setNoteForm({
      date: note.date,
      topic: note.topic,
      scriptureReference: note.scriptureReference,
      notes: note.notes
    });
    setEditingNote(note);
    setShowNoteDialog(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-8 pb-8 rounded-b-3xl shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
            <BookOpen className="w-6 h-6 text-tan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Bible & Notes</h1>
            <p className="text-tan-400 text-sm font-medium">Read, Search & Take Notes</p>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex bg-white/10 rounded-xl p-1">
          <button
            onClick={() => setActiveTab('bible')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'bible' ? 'bg-white text-navy-900' : 'text-white/80'
            }`}
          >
            <Book className="w-4 h-4" />
            Bible
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'notes' ? 'bg-white text-navy-900' : 'text-white/80'
            }`}
          >
            <FileText className="w-4 h-4" />
            Sermon Notes
          </button>
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {activeTab === 'bible' ? (
          <>
            {/* Translation & Search */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-4 space-y-3">
                {/* Translation Selector */}
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-gray-600 whitespace-nowrap">Translation:</Label>
                  <Select value={translation} onValueChange={setTranslation}>
                    <SelectTrigger className="h-10 bg-gray-50 border-gray-200 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kjv">King James Version (KJV)</SelectItem>
                      <SelectItem value="web">World English Bible (WEB)</SelectItem>
                      <SelectItem value="bbe">Bible in Basic English (BBE)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Search */}
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search (e.g., John 3:16, Psalm 23)"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-10 bg-gray-50 border-gray-200 rounded-xl"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="h-10 px-4 bg-tan-500 hover:bg-tan-600 rounded-xl"
                    disabled={searching}
                  >
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Go'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Search Results */}
            {searchResults && (
              <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-4">
                  {searchResults.error ? (
                    <p className="text-red-500 text-sm">{searchResults.error}</p>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-navy-900">{searchResults.reference}</h3>
                        <Badge className="bg-tan-100 text-tan-700">{translation.toUpperCase()}</Badge>
                      </div>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {searchResults.text}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-gray-500"
                        onClick={() => setSearchResults(null)}
                      >
                        <X className="w-4 h-4 mr-1" /> Clear
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Book Selector or Reading View */}
            {!showReadingView ? (
              <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-4">
                  <h3 className="font-bold text-navy-900 mb-3">Select a Book</h3>
                  <p className="text-sm text-gray-500 mb-4">Tap a book to select a chapter</p>
                  
                  {/* Old Testament */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-tan-600 uppercase tracking-wide mb-2">Old Testament</p>
                    <div className="grid grid-cols-3 gap-2">
                      {BIBLE_BOOKS.oldTestament.map((book) => (
                        <button
                          key={book.name}
                          onClick={() => handleBookClick(book)}
                          className="p-2.5 text-xs rounded-lg text-left transition-all bg-gray-100 text-gray-700 hover:bg-navy-900 hover:text-white flex items-center justify-between"
                        >
                          <span className="truncate">{book.name}</span>
                          <ChevronRight className="w-3 h-3 flex-shrink-0 ml-1" />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* New Testament */}
                  <div>
                    <p className="text-xs font-semibold text-tan-600 uppercase tracking-wide mb-2">New Testament</p>
                    <div className="grid grid-cols-3 gap-2">
                      {BIBLE_BOOKS.newTestament.map((book) => (
                        <button
                          key={book.name}
                          onClick={() => handleBookClick(book)}
                          className="p-2.5 text-xs rounded-lg text-left transition-all bg-gray-100 text-gray-700 hover:bg-navy-900 hover:text-white flex items-center justify-between"
                        >
                          <span className="truncate">{book.name}</span>
                          <ChevronRight className="w-3 h-3 flex-shrink-0 ml-1" />
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Reading View */
              <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-4">
                  {loadingChapter ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-tan-500" />
                    </div>
                  ) : chapterContent?.error ? (
                    <div className="text-center py-8">
                      <p className="text-red-500 mb-4">{chapterContent.error}</p>
                      <Button variant="outline" onClick={backToBooks}>
                        Back to Books
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Chapter Header */}
                      <div className="flex items-center justify-between mb-4">
                        <button
                          onClick={backToBooks}
                          className="text-tan-600 text-sm font-medium flex items-center gap-1"
                        >
                          <ChevronLeft className="w-4 h-4" /> Books
                        </button>
                        <div className="text-center">
                          <h3 className="font-bold text-navy-900">{chapterContent?.reference}</h3>
                          <Badge className="bg-tan-100 text-tan-700 text-xs">{translation.toUpperCase()}</Badge>
                        </div>
                        <button
                          onClick={() => {
                            setShowChapterDialog(true);
                          }}
                          className="text-tan-600 text-sm font-medium"
                        >
                          Ch. {selectedChapter}
                        </button>
                      </div>

                      {/* Chapter Text with Verse Numbers */}
                      <div className="space-y-2">
                        {chapterContent?.verses ? (
                          chapterContent.verses.map((verse) => (
                            <p key={verse.verse} className="text-gray-700 leading-relaxed text-base">
                              <span className="text-tan-600 font-bold text-sm align-super mr-1">
                                {verse.verse}
                              </span>
                              {verse.text.trim()}
                            </p>
                          ))
                        ) : (
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line text-base">
                            {chapterContent?.text}
                          </p>
                        )}
                      </div>

                      {/* Navigation */}
                      <div className="flex items-center justify-between mt-6 pt-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={goToPrevChapter}
                          className="rounded-xl"
                          disabled={selectedChapter === 1 && ALL_BOOKS.findIndex(b => b.name === selectedBook?.name) === 0}
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                        </Button>
                        <span className="text-sm text-gray-500">
                          {selectedChapter} / {selectedBook?.chapters}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={goToNextChapter}
                          className="rounded-xl"
                          disabled={selectedChapter === selectedBook?.chapters && ALL_BOOKS.findIndex(b => b.name === selectedBook?.name) === ALL_BOOKS.length - 1}
                        >
                          Next <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          /* Sermon Notes Tab */
          <>
            {/* Add Note Button */}
            <Button
              onClick={() => {
                resetNoteForm();
                setShowNoteDialog(true);
              }}
              className="w-full h-12 bg-tan-500 hover:bg-tan-600 text-white rounded-xl font-semibold text-base shadow-md"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Sermon Note
            </Button>

            {/* Notes List */}
            {loadingNotes ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-tan-500" />
              </div>
            ) : sermonNotes.length === 0 ? (
              <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No sermon notes yet</p>
                  <p className="text-sm text-gray-400 mt-1">Tap "New Sermon Note" to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {sermonNotes.map((note) => (
                  <Card key={note.id} className="border-0 shadow-lg rounded-2xl overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="w-4 h-4 text-tan-600" />
                            <span className="text-sm text-gray-500">
                              {new Date(note.date).toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                          <h3 className="font-bold text-navy-900 text-lg">{note.topic}</h3>
                          {note.scriptureReference && (
                            <Badge className="bg-tan-100 text-tan-700 mt-1">
                              {note.scriptureReference}
                            </Badge>
                          )}
                          <p className="text-gray-600 text-sm mt-2 line-clamp-2">{note.notes}</p>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openEditNote(note)}
                          >
                            <Edit className="w-4 h-4 text-gray-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Sermon Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900">
              {editingNote ? 'Edit Sermon Note' : 'New Sermon Note'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveNote} className="space-y-4">
            <div>
              <Label className="text-navy-900">Date</Label>
              <Input
                type="date"
                value={noteForm.date}
                onChange={(e) => setNoteForm({ ...noteForm, date: e.target.value })}
                className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1"
                required
              />
            </div>
            <div>
              <Label className="text-navy-900">Sermon Topic / Title</Label>
              <Input
                placeholder="Enter the sermon topic"
                value={noteForm.topic}
                onChange={(e) => setNoteForm({ ...noteForm, topic: e.target.value })}
                className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1"
                required
              />
            </div>
            <div>
              <Label className="text-navy-900">Scripture Reference</Label>
              <Input
                placeholder="e.g., John 3:16, Romans 8:28"
                value={noteForm.scriptureReference}
                onChange={(e) => setNoteForm({ ...noteForm, scriptureReference: e.target.value })}
                className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-navy-900">Notes</Label>
              <Textarea
                placeholder="Write your sermon notes here..."
                value={noteForm.notes}
                onChange={(e) => setNoteForm({ ...noteForm, notes: e.target.value })}
                className="min-h-[150px] bg-gray-50 border-gray-200 rounded-xl mt-1 resize-none"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-navy-900 hover:bg-navy-800 text-white rounded-xl"
              disabled={savingNote}
            >
              {savingNote ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingNote ? 'Update Note' : 'Save Note'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Chapter Selection Dialog */}
      <Dialog open={showChapterDialog} onOpenChange={setShowChapterDialog}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900 flex items-center gap-2">
              <Book className="w-5 h-5 text-tan-600" />
              {selectedBook?.name}
            </DialogTitle>
          </DialogHeader>
          <div>
            <p className="text-sm text-gray-500 mb-4">Select a chapter to read</p>
            <div className="grid grid-cols-5 gap-2">
              {selectedBook && Array.from({ length: selectedBook.chapters }, (_, i) => i + 1).map((chapter) => (
                <button
                  key={chapter}
                  onClick={() => handleChapterClick(chapter)}
                  className="p-3 text-sm rounded-xl bg-tan-100 text-tan-700 hover:bg-navy-900 hover:text-white transition-all font-semibold"
                >
                  {chapter}
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default BiblePage;
